<template>
  <div class="content-main relative">
    <template v-if="openedPayment !== ''">
      <div class="flex justify-center items-center w-full pt-10">
        <button
          class="btn-dark-button absolute right-2 top-2"
          @click="openedPayment = ''"
        >
          Close (Q)
        </button>
        <PaymentOrderModal
          @closedPayment="openedPayment = ''"
          @openPayment="open"
          :paymentId="openedPayment"
        />
      </div>
    </template>
    <template v-else>
      <div class="content">
        <!-- Tabs -->
        <div class="tabs-bar">
          <button
            class="tab-button"
            :class="{ active: activeTab === 'payments' }"
            @click="activeTab = 'payments'"
          >
            <i class="mdi mdi-credit-card-outline"></i>
            Payments
          </button>
          <button
            class="tab-button"
            :class="{ active: activeTab === 'stats' }"
            @click="activeTab = 'stats'"
          >
            <i class="mdi mdi-chart-bar"></i>
            Stats
          </button>
        </div>

        <!-- Search Bar -->
        <div class="search-bar">
          <div class="search-wrapper">
            <i class="mdi mdi-magnify search-icon"></i>
            <input
              type="text"
              v-model="filters.search"
              placeholder="Search payments by order number, cardholder, email..."
              class="search-input"
            />
            <button
              v-if="filters.search"
              @click="filters.search = ''"
              class="clear-search"
            >
              <i class="mdi mdi-close"></i>
            </button>
          </div>
        </div>

        <!-- Filters -->
        <div class="filters-bar">
          <div class="filter-group">
            <label class="filter-label" for="department">Department</label>
            <select
              id="department"
              class="filter-input"
              v-model="filters.department"
            >
              <option value="ALL">All departments</option>
              <option
                v-for="department in departments"
                :key="department.code"
                :value="department.code"
              >
                {{ department.code }} - {{ department.name }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label" for="from-date">From</label>
            <input
              id="from-date"
              type="date"
              class="filter-input"
              v-model="filters.fromDate"
            />
          </div>
          <div class="filter-group">
            <label class="filter-label" for="to-date">To</label>
            <input
              id="to-date"
              type="date"
              class="filter-input"
              v-model="filters.toDate"
            />
          </div>
          <div class="filter-group">
            <label class="filter-label" for="payment-type">Payment Type</label>
            <select
              id="payment-type"
              class="filter-input"
              v-model="filters.paymentType"
            >
              <option value="">All</option>
              <option v-for="type in paymentTypes" :key="type" :value="type">
                {{ type }}
              </option>
            </select>
          </div>
          <div class="filter-group">
            <label class="filter-label" for="status-success">Status</label>
            <select
              id="status-success"
              class="filter-input"
              v-model="filters.statusSuccess"
            >
              <option value="">All</option>
              <option value="1">Success</option>
              <option value="0">Error</option>
            </select>
          </div>
          <div class="filter-group presets">
            <label class="filter-label">Quick range</label>
            <div class="preset-buttons">
              <button class="preset-button" @click="applyPreset('thisMonth')">
                This month
              </button>
              <button class="preset-button" @click="applyPreset('lastMonth')">
                Last month
              </button>
              <button class="preset-button" @click="applyPreset('last30')">
                Last 30d
              </button>
              <button class="preset-button" @click="applyPreset('thisYear')">
                This year
              </button>
              <button class="preset-button clear" @click="clearFilters()">
                Clear
              </button>
            </div>
          </div>
        </div>

        <!-- Payments tab -->
        <template v-if="activeTab === 'payments'">
          <div class="header relative">
            Payments for orders by users
          </div>
          <div class="table">
            <!-- Desktop Table View (hidden on mobile) -->
            <div class="hidden md:block overflow-x-auto">
              <table class="default-table" v-if="!loading">
                <thead>
                  <tr class="table100-head">
                    <th
                      v-for="headerItem in tableHeaders"
                      :key="headerItem.value"
                      @click="changeArrowDir(headerItem.value)"
                    >
                      {{ headerItem.displayText }}
                      <span v-if="filters.orderCol == headerItem.value">
                        <img
                          :src="
                            require(`@/assets/icons/${arrowDirection}-arrow.svg`)
                          "
                        />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="payment in payments"
                    :key="payment.id"
                    @click="open(payment.id)"
                    :class="{
                      selected: selectedCustomer == payment.id,
                      'refund-row': isRefund(payment)
                    }"
                  >
                    <td>
                      {{ payment.cardholderName }}
                      <br />
                      {{ payment.paypalPayerEmail }}
                    </td>
                    <td>
                      {{ payment.dateTransaction | dateWithHours }}
                    </td>
                    <td>
                      {{ payment.orderNumber }}
                    </td>
                    <td>
                      <span
                        v-if="payment.departamentoCodigo"
                        class="dept-badge"
                      >
                        {{ payment.departamentoCodigo }}
                      </span>
                      <span v-else class="text-gray-400">-</span>
                      <div
                        v-if="payment.departamentoNombre"
                        class="text-xs text-gray-500"
                      >
                        {{ payment.departamentoNombre }}
                      </div>
                    </td>
                    <td>
                      <span :class="{ 'refund-type': isRefund(payment) }">{{
                        payment.paymentType
                      }}</span>
                    </td>
                    <td>
                      <template v-if="payment.transactionTag">
                        {{ payment.transactionTag }}
                      </template>
                      <template v-else-if="payment.paypalPayerId">
                        {{ payment.paypalPayerId }}
                      </template>
                      <template v-else-if="payment.transactionId">
                        {{ payment.transactionId }}
                      </template>
                    </td>
                    <td>
                      <template v-if="isRefund(payment)">
                        <span class="refund-amount">{{
                          refundMoney(payment.amount)
                        }}</span>
                        <span class="refund-badge">↩ REFUND</span>
                      </template>
                      <template v-else>${{ payment.amount }}</template>
                    </td>
                    <td>
                      <div
                        v-if="payment.statusSuccess == '1'"
                        class="text-green-success"
                      >
                        Success
                      </div>
                      <div v-else class="text-red-cancel">
                        Error
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Mobile Card View (hidden on desktop) -->
            <div class="md:hidden space-y-3 p-2" v-if="!loading">
              <div
                v-for="payment in payments"
                :key="payment.id"
                @click="open(payment.id)"
                class="bg-white border rounded-lg p-4 shadow-sm cursor-pointer active:bg-gray-50"
                :class="{
                  'border-green-300 bg-green-50':
                    payment.statusSuccess == '1' && !isRefund(payment),
                  'border-red-300 bg-red-50': payment.statusSuccess != '1',
                  'refund-card': isRefund(payment)
                }"
              >
                <!-- Header: Order Number and Status -->
                <div
                  class="flex justify-between items-center mb-3 pb-3 border-b"
                >
                  <div>
                    <div class="text-xs text-gray-500 uppercase">
                      Order Number
                    </div>
                    <div class="font-bold text-lg text-gray-800">
                      {{ payment.orderNumber }}
                    </div>
                  </div>
                  <div>
                    <span
                      class="px-3 py-1 rounded-full text-xs font-semibold"
                      :class="{
                        'bg-green-500 text-white': payment.statusSuccess == '1',
                        'bg-red-500 text-white': payment.statusSuccess != '1'
                      }"
                    >
                      {{ payment.statusSuccess == "1" ? "Success" : "Error" }}
                    </span>
                  </div>
                </div>

                <!-- Cardholder Info -->
                <div class="mb-3">
                  <div class="text-xs text-gray-500 uppercase mb-1">
                    Cardholder
                  </div>
                  <div class="text-sm text-gray-800 font-medium">
                    {{ payment.cardholderName }}
                  </div>
                  <div
                    v-if="payment.paypalPayerEmail"
                    class="text-sm text-gray-600"
                  >
                    {{ payment.paypalPayerEmail }}
                  </div>
                </div>

                <!-- Payment Details Grid -->
                <div class="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <div class="text-xs text-gray-500 uppercase">Amount</div>
                    <div v-if="isRefund(payment)" class="text-lg">
                      <span class="refund-amount">{{
                        refundMoney(payment.amount)
                      }}</span>
                      <div><span class="refund-badge">↩ REFUND</span></div>
                    </div>
                    <div v-else class="text-lg font-bold text-blue-600">
                      ${{ payment.amount }}
                    </div>
                  </div>
                  <div>
                    <div class="text-xs text-gray-500 uppercase">Type</div>
                    <div class="text-sm font-medium text-gray-800">
                      {{ payment.paymentType }}
                    </div>
                  </div>
                </div>

                <!-- Department -->
                <div class="mb-3">
                  <div class="text-xs text-gray-500 uppercase">Department</div>
                  <div class="text-sm text-gray-800">
                    <template v-if="payment.departamentoCodigo">
                      {{ payment.departamentoCodigo }}
                      <span
                        v-if="payment.departamentoNombre"
                        class="text-gray-500"
                      >
                        - {{ payment.departamentoNombre }}
                      </span>
                    </template>
                    <template v-else>-</template>
                  </div>
                </div>

                <!-- Date -->
                <div class="mb-3">
                  <div class="text-xs text-gray-500 uppercase">Date</div>
                  <div class="text-sm text-gray-800">
                    {{ payment.dateTransaction | dateWithHours }}
                  </div>
                </div>

                <!-- Transaction ID -->
                <div class="bg-gray-100 p-2 rounded">
                  <div class="text-xs text-gray-500 uppercase mb-1">
                    Transaction ID
                  </div>
                  <div class="text-xs text-gray-700 break-all">
                    <template v-if="payment.transactionTag">
                      {{ payment.transactionTag }}
                    </template>
                    <template v-else-if="payment.paypalPayerId">
                      {{ payment.paypalPayerId }}
                    </template>
                    <template v-else-if="payment.transactionId">
                      {{ payment.transactionId }}
                    </template>
                    <template v-else>
                      N/A
                    </template>
                  </div>
                </div>
              </div>
            </div>

            <div v-else>
              Loading...
            </div>
          </div>
          <div class="footer">
            <div class="totals">
              showing {{ filters.limitPerPage * (filters.page - 1) }} to
              {{ toQuantity() }} from {{ totalItems }} results
            </div>
            <div class="pages">
              <select
                v-model="paymentsByPage"
                @change="
                  (filters.limitPerPage = paymentsByPage), (filters.page = 1)
                "
                class="md:w-auto w-16 text-sm"
              >
                <option value="20">20</option>
                <option value="50">50</option>
                <option value="100">100</option>
                <option value="500">500</option>
              </select>
              <span class="hidden md:inline">Per Page</span>
              <span class="md:hidden text-xs">/ page</span>
            </div>
            <div class="pages">
              <div>
                <a
                  :class="{ disabled: filters.page == 0 }"
                  class="page-link"
                  @click="previous()"
                  >Prev</a
                >
              </div>
              <div class="hidden md:block">Page {{ filters.page }}</div>
              <div class="md:hidden">{{ filters.page }}</div>
              <div>
                <a
                  :class="{
                    disabled: totalPages > filters.page
                  }"
                  class="page-link"
                  @click="next()"
                  >Next</a
                >
              </div>
            </div>
          </div>
        </template>

        <!-- Stats tab -->
        <template v-else>
          <div class="header relative">
            Payments stats
            <span class="text-sm text-gray-500 ml-2">
              {{ statsRangeLabel }}
            </span>
          </div>

          <div v-if="statsLoading" class="p-4">
            Loading...
          </div>
          <div v-else-if="!stats" class="p-4">
            No data
          </div>
          <div v-else class="stats-content">
            <!-- Summary cards -->
            <div class="stats-cards">
              <div class="stat-card">
                <div class="stat-label">Collected (gross)</div>
                <div class="stat-value text-green-700">
                  {{ money(stats.summary.grossAmount) }}
                </div>
                <div class="stat-hint">
                  {{ stats.summary.successfulPayments }} payments
                </div>
              </div>
              <div class="stat-card stat-card--refund">
                <div class="stat-label">↩ Refunds</div>
                <div class="stat-value refund-text">
                  {{ refundMoney(stats.summary.refundsAmount) }}
                </div>
                <div class="stat-hint">
                  {{ stats.summary.refundsCount }} refunds ·
                  {{
                    percentOf(
                      stats.summary.refundsAmount,
                      stats.summary.grossAmount
                    )
                  }}
                  of gross
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Net collected</div>
                <div class="stat-value">
                  {{ money(stats.summary.successfulAmount) }}
                </div>
                <div class="stat-hint">gross - refunds</div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Payment fees</div>
                <div class="stat-value text-blue-700">
                  {{ money(stats.summary.totalFees) }}
                </div>
                <div class="stat-hint">
                  {{ stats.summary.ordersWithFees }} orders with fees
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Net (after refunds and fees)</div>
                <div class="stat-value">
                  {{
                    money(
                      stats.summary.successfulAmount - stats.summary.totalFees
                    )
                  }}
                </div>
                <div class="stat-hint">
                  {{ stats.summary.totalOrders }} orders
                </div>
              </div>
              <div class="stat-card">
                <div class="stat-label">Failed</div>
                <div class="stat-value text-red-cancel">
                  {{ money(stats.summary.failedAmount) }}
                </div>
                <div class="stat-hint">
                  {{ stats.summary.failedPayments }} payments
                </div>
              </div>
            </div>

            <!-- By payment type -->
            <div class="stats-section">
              <div class="stats-section-title">By payment type</div>
              <div class="overflow-x-auto">
                <table class="default-table">
                  <thead>
                    <tr class="table100-head">
                      <th>Payment type</th>
                      <th>Payments</th>
                      <th>Collected</th>
                      <th>Refunds</th>
                      <th>Net</th>
                      <th>Failed</th>
                      <th>Failed amount</th>
                      <th>% of net</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in stats.byPaymentType"
                      :key="row.paymentType"
                      class="clickable-row"
                      @click="filterByType(row.paymentType)"
                    >
                      <td>{{ row.paymentType }}</td>
                      <td>{{ row.successfulPayments }}</td>
                      <td>{{ money(row.grossAmount) }}</td>
                      <td>
                        <template v-if="Number(row.refundsCount) > 0">
                          <span class="refund-text">{{
                            refundMoney(row.refundsAmount)
                          }}</span>
                          <span class="text-xs text-gray-500">
                            ({{ row.refundsCount }})</span
                          >
                        </template>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td class="font-semibold">
                        {{ money(row.successfulAmount) }}
                      </td>
                      <td>{{ row.failedPayments }}</td>
                      <td>{{ money(row.failedAmount) }}</td>
                      <td>{{ percentOfTotal(row.successfulAmount) }}</td>
                    </tr>
                    <tr v-if="!stats.byPaymentType.length">
                      <td colspan="8">No payments in range</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- By department -->
            <div class="stats-section">
              <div class="stats-section-title">By department</div>
              <div class="overflow-x-auto">
                <table class="default-table">
                  <thead>
                    <tr class="table100-head">
                      <th>Department</th>
                      <th>Payments</th>
                      <th>Collected</th>
                      <th>Refunds</th>
                      <th>Net</th>
                      <th>% of net</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="row in stats.byDepartment"
                      :key="row.departamentoCodigo + '-' + row.departamentoId"
                    >
                      <td>
                        <span class="dept-badge">
                          {{ row.departamentoCodigo }}
                        </span>
                        <span class="text-xs text-gray-500 ml-1">
                          {{ row.departamentoNombre }}
                        </span>
                      </td>
                      <td>{{ row.successfulPayments }}</td>
                      <td>{{ money(row.grossAmount) }}</td>
                      <td>
                        <template v-if="Number(row.refundsCount) > 0">
                          <span class="refund-text">{{
                            refundMoney(row.refundsAmount)
                          }}</span>
                          <span class="text-xs text-gray-500">
                            ({{ row.refundsCount }})</span
                          >
                        </template>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td class="font-semibold">
                        {{ money(row.successfulAmount) }}
                      </td>
                      <td>{{ percentOfTotal(row.successfulAmount) }}</td>
                    </tr>
                    <tr v-if="!stats.byDepartment.length">
                      <td colspan="6">No payments in range</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- By period -->
            <div class="stats-section">
              <div class="stats-section-title">
                By period
                <select
                  v-model="statsGroupBy"
                  class="filter-input inline-select"
                >
                  <option value="day">Day</option>
                  <option value="month">Month</option>
                  <option value="year">Year</option>
                </select>
              </div>
              <div class="overflow-x-auto">
                <table class="default-table">
                  <thead>
                    <tr class="table100-head">
                      <th>Period</th>
                      <th
                        v-for="type in periodTypes"
                        :key="'head-' + type"
                        class="text-right"
                      >
                        {{ type }}
                      </th>
                      <th class="text-right refund-text">↩ Refunds</th>
                      <th class="text-right">Net total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="row in periodRows" :key="row.period">
                      <td>{{ row.period }}</td>
                      <td
                        v-for="type in periodTypes"
                        :key="row.period + '-' + type"
                        class="text-right"
                      >
                        {{ money(row.amounts[type] || 0) }}
                      </td>
                      <td class="text-right">
                        <span v-if="row.refunds > 0" class="refund-text">{{
                          refundMoney(row.refunds)
                        }}</span>
                        <span v-else class="text-gray-400">-</span>
                      </td>
                      <td class="text-right font-semibold">
                        {{ money(row.total) }}
                      </td>
                    </tr>
                    <tr v-if="!periodRows.length">
                      <td :colspan="periodTypes.length + 3">
                        No payments in range
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import {
  getPayments,
  getPaymentsStats,
  getPaymentTypes
} from "@/api/store/PaymentsService.js";
import { createQueryString } from "@/api/store/OrdersService.js";
import { getDepartments } from "@/api/store/DepartmentsService.js";
import PaymentOrderModal from "@/components/payments/PaymentOrderModal.vue";

export default {
  name: "PaymentsPage",
  components: {
    PaymentOrderModal
  },
  data() {
    return {
      publicPath: process.env.VUE_APP_IMG_BASE_URL,
      tableHeaders: [
        {
          displayText: "Cardholder",
          value: "email"
        },
        {
          displayText: "Date",
          value: "date"
        },
        {
          displayText: "Order Number",
          value: "orderNumber"
        },
        {
          displayText: "Department",
          value: "departamento"
        },
        {
          displayText: "Payment Type",
          value: "payment_type"
        },
        {
          displayText: "Payment Id-tag",
          value: "paymentId"
        },
        {
          displayText: "Payment Amount",
          value: "amount"
        },
        {
          displayText: "Status",
          value: "status"
        }
      ],
      filters: {
        page: 1,
        limitPerPage: 100,
        orderCol: "name",
        orderDir: "desc",
        search: "",
        fromDate: "",
        toDate: "",
        paymentType: "",
        statusSuccess: "",
        // El departamento se elige aquí, no con el selector global del header.
        department: "ALL"
      },
      activeTab: "payments",
      paymentsByPage: 100,
      payments: [],
      departments: [],
      paymentTypes: [],
      stats: null,
      statsLoading: false,
      statsGroupBy: "month",
      totalItems: 0,
      totalPages: 0,
      loading: true,
      selectedCustomer: null,
      openedPayment: "",
      showPaymentModal: false
    };
  },
  watch: {
    openedPayment() {
      if (!this.loading) {
        const query = {
          ...this.$route.query,
          openedPayment: this.openedPayment
        };
        this.$router.replace({ query }).catch(() => {});
      }
    },
    activeTab(tab) {
      const query = { ...this.$route.query, tab };
      this.$router.replace({ query }).catch(() => {});
      if (tab === "stats" && !this.stats) {
        this.loadStats();
      }
    },
    statsGroupBy() {
      if (this.activeTab === "stats") {
        this.loadStats();
      }
    },
    "filters.department"() {
      // Cada departamento tiene sus propios tipos de pago.
      this.loadPaymentTypes();
    },
    filters: {
      deep: true,
      handler() {
        if (!this.loading) {
          const query = {
            ...this.$route.query,
            search: createQueryString(this.filters)
          };
          this.$router.replace({ query }).catch(() => {});
          this.loadPayments();
          // Las stats comparten filtros con el listado, pero solo se recargan
          // cuando su pestaña está visible (o ya fueron cargadas antes).
          if (this.activeTab === "stats" || this.stats) {
            this.loadStats();
          }
        }
      }
    }
  },
  computed: {
    arrowDirection() {
      return this.filters.orderDir == "desc" ? "down" : "up";
    },
    statsRangeLabel() {
      const from = this.filters.fromDate || "beginning";
      const to = this.filters.toDate || "today";
      const department =
        this.filters.department === "ALL"
          ? "All departments"
          : this.filters.department;
      return `${department} · ${from} → ${to}`;
    },
    // Tipos de pago presentes en el desglose por período, para armar las
    // columnas de la tabla pivote.
    periodTypes() {
      if (!this.stats) return [];
      const types = new Set();
      this.stats.byPeriod.forEach(row => types.add(row.paymentType));
      return Array.from(types);
    },
    // Pivote: una fila por período con lo cobrado (bruto) de cada tipo de
    // pago, los reembolsos del período y el total neto.
    periodRows() {
      if (!this.stats) return [];
      const rows = {};
      this.stats.byPeriod.forEach(row => {
        if (!rows[row.period]) {
          rows[row.period] = {
            period: row.period,
            amounts: {},
            refunds: 0,
            total: 0
          };
        }
        const gross = Number(row.grossAmount) || 0;
        rows[row.period].amounts[row.paymentType] =
          (rows[row.period].amounts[row.paymentType] || 0) + gross;
        rows[row.period].refunds += Number(row.refundsAmount) || 0;
        rows[row.period].total += Number(row.successfulAmount) || 0;
      });
      return Object.values(rows).sort((a, b) => (a.period < b.period ? 1 : -1));
    }
  },
  mounted() {
    let search = new URLSearchParams(this.$route.query.search);
    if (search) {
      for (let p of search) {
        this.filters[p[0]] = p[1];
      }
    }
    if (this.$route.query.tab === "stats") {
      this.activeTab = "stats";
    }
    let openedPayment = this.$route.query.openedPayment;
    if (openedPayment && openedPayment != "") {
      this.open(openedPayment);
    }
    this.loading = false;
    this.loadDepartments();
    this.loadPayments();
    this.loadPaymentTypes();
    if (this.activeTab === "stats") {
      this.loadStats();
    }
  },
  methods: {
    logout() {
      this.$store.dispatch("auth/logOut");
      this.$router.push("/");
    },
    async loadPayments() {
      const res = await getPayments(this.filters).catch(() => {
        this.$toast.open({
          message: "Error getting payments",
          type: "Error",
          position: "top-right"
        });
      });
      if (res && res.payments) {
        this.payments = res.payments;
        this.totalItems = res.totalItems;
        this.totalPages = res.totalPages;
      }
      this.loading = false;
    },
    // La lista sale del store general si ya está cargada; si no, se pide.
    async loadDepartments() {
      const stored = this.$store.getters["general/getDepartments"];
      if (stored && stored.length) {
        this.departments = stored;
        return;
      }
      this.departments = await getDepartments();
    },
    async loadStats() {
      this.statsLoading = true;
      const res = await getPaymentsStats({
        search: this.filters.search,
        fromDate: this.filters.fromDate,
        toDate: this.filters.toDate,
        paymentType: this.filters.paymentType,
        statusSuccess: this.filters.statusSuccess,
        department: this.filters.department,
        groupBy: this.statsGroupBy
      }).catch(() => {
        this.$toast.open({
          message: "Error getting payments stats",
          type: "Error",
          position: "top-right"
        });
      });
      if (res && res.summary) {
        this.stats = res;
      }
      this.statsLoading = false;
    },
    async loadPaymentTypes() {
      const res = await getPaymentTypes(this.filters.department).catch(
        () => null
      );
      if (res && res.types) {
        this.paymentTypes = res.types;
        // Si el tipo elegido no existe en el departamento actual, se limpia
        // para no dejar el listado vacío sin motivo aparente.
        if (
          this.filters.paymentType &&
          !this.paymentTypes.includes(this.filters.paymentType)
        ) {
          this.filters.paymentType = "";
        }
      }
    },
    money(value) {
      const number = Number(value) || 0;
      return `$${number.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}`;
    },
    // Reembolsos: se muestran en rojo con signo menos
    refundMoney(value) {
      return `−${this.money(Math.abs(Number(value) || 0))}`;
    },
    percentOf(value, total) {
      const t = Number(total) || 0;
      if (!t) return "0%";
      return `${((Number(value) / t) * 100).toFixed(1)}%`;
    },
    percentOfTotal(value) {
      const total = Number(this.stats?.summary?.successfulAmount) || 0;
      if (!total) return "0%";
      return `${(((Number(value) || 0) * 100) / total).toFixed(1)}%`;
    },
    // Click en una fila del desglose: filtra por ese tipo y salta al listado.
    filterByType(type) {
      this.filters.paymentType = type;
      this.filters.page = 1;
      this.activeTab = "payments";
    },
    applyPreset(preset) {
      const now = new Date();
      const format = date => date.toISOString().slice(0, 10);
      let from = null;
      let to = null;
      switch (preset) {
        case "thisMonth":
          from = new Date(now.getFullYear(), now.getMonth(), 1);
          to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case "lastMonth":
          from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          to = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case "last30":
          from = new Date(now.getTime() - 29 * 24 * 60 * 60 * 1000);
          to = now;
          break;
        case "thisYear":
          from = new Date(now.getFullYear(), 0, 1);
          to = new Date(now.getFullYear(), 11, 31);
          break;
      }
      // Se construye la fecha en horario local y se compensa el offset para
      // que toISOString no desplace el día.
      const toLocalISO = date =>
        format(new Date(date.getTime() - date.getTimezoneOffset() * 60000));
      this.filters.page = 1;
      this.filters.fromDate = toLocalISO(from);
      this.filters.toDate = toLocalISO(to);
    },
    clearFilters() {
      this.filters.page = 1;
      this.filters.fromDate = "";
      this.filters.toDate = "";
      this.filters.paymentType = "";
      this.filters.statusSuccess = "";
      this.filters.search = "";
      this.filters.department = "ALL";
    },
    previous() {
      if (this.filters.page > 1) {
        this.filters.page = this.filters.page - 1;
      }
    },
    next() {
      if (this.totalItems >= this.filters.limitPerPage * this.filters.page) {
        this.filters.page = this.filters.page + 1;
      }
    },
    toQuantity() {
      if (this.qtyPerPage * this.page > this.totalItems) {
        return this.payments.length;
      } else {
        return (
          this.filters.limitPerPage * (this.filters.page - 1) +
          this.payments.length
        );
      }
    },
    changeArrowDir(headerValue) {
      this.filters.page = 1;
      if (headerValue == this.filters.orderCol) {
        this.filters.orderDir =
          this.filters.orderDir == "desc" ? "asc" : "desc";
      } else {
        this.filters.orderCol = headerValue;
        this.filters.orderDir = "desc";
      }
    },
    // Reembolsos: filas negativas (ver Stripex::admin_refund en usa-web-api)
    isRefund(payment) {
      return Number(payment.amount) < 0;
    },
    open(customerID) {
      this.openedPayment = customerID;
      // this.showPaymentModal = true;
    }
  }
};
</script>
<style lang="scss" scoped>
@import "@/assets/style/css/generic-variables.scss";

.content-main {
  display: flex;
  flex-direction: column;
  height: 100%;

  .tabs-bar {
    display: flex;
    gap: 4px;
    padding: 10px 15px 0;
    background: white;
    border-bottom: 1px solid #e5e7eb;

    @media (max-width: 768px) {
      padding: 8px 10px 0;
    }

    .tab-button {
      padding: 8px 18px;
      border: 1px solid transparent;
      border-bottom: none;
      border-radius: 8px 8px 0 0;
      background: #f3f4f6;
      color: #4b5563;
      font-size: 14px;
      cursor: pointer;

      i {
        margin-right: 4px;
      }

      &.active {
        background: white;
        color: $link-color-1;
        border-color: #e5e7eb;
        font-weight: 600;
      }
    }
  }

  .search-bar {
    background: white;
    padding: 15px;
    border-bottom: 1px solid #e5e7eb;

    @media (max-width: 768px) {
      padding: 10px;
    }

    .search-wrapper {
      position: relative;
      max-width: 600px;

      @media (max-width: 768px) {
        max-width: 100%;
      }

      .search-icon {
        position: absolute;
        left: 12px;
        top: 50%;
        transform: translateY(-50%);
        color: #6b7280;
        font-size: 20px;
        pointer-events: none;
      }

      .search-input {
        width: 100%;
        padding: 10px 40px 10px 42px;
        border: 1px solid #d1d5db;
        border-radius: 8px;
        font-size: 15px;
        transition: border-color 0.2s, box-shadow 0.2s;

        @media (max-width: 768px) {
          font-size: 16px; /* Prevent zoom on iOS */
          padding: 12px 40px 12px 42px;
        }

        &:focus {
          outline: none;
          border-color: $link-color-1;
          box-shadow: 0 0 0 3px rgba(18, 110, 106, 0.1);
        }

        &::placeholder {
          color: #9ca3af;
        }
      }

      .clear-search {
        position: absolute;
        right: 8px;
        top: 50%;
        transform: translateY(-50%);
        background: transparent;
        border: none;
        padding: 4px;
        cursor: pointer;
        color: #6b7280;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 4px;
        transition: background-color 0.2s;

        &:hover {
          background-color: #f3f4f6;
        }

        i {
          font-size: 18px;
        }
      }
    }
  }

  .filters-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: flex-end;
    background: white;
    padding: 12px 15px;
    border-bottom: 1px solid #e5e7eb;

    @media (max-width: 768px) {
      padding: 10px;
      gap: 8px;
    }

    .filter-group {
      display: flex;
      flex-direction: column;
      gap: 4px;

      &.presets {
        flex: 1;
        min-width: 220px;
      }
    }

    .preset-buttons {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .preset-button {
      padding: 6px 10px;
      border: 1px solid #d1d5db;
      border-radius: 6px;
      background: #f9fafb;
      font-size: 12px;
      color: #374151;
      cursor: pointer;

      &:hover {
        background: #f3f4f6;
      }

      &.clear {
        color: #b91c1c;
        border-color: #fecaca;
      }
    }
  }

  .filter-label {
    font-size: 12px;
    color: #6b7280;
    text-transform: uppercase;
  }

  .filter-input {
    border: 1px solid #d1d5db;
    border-radius: 6px;
    padding: 6px 10px;
    font-size: 14px;

    &:focus {
      outline: none;
      border-color: $link-color-1;
    }
  }

  .content {
    padding: 5px;
    width: 100%;

    @media (max-width: 768px) {
      padding: 0;
    }

    .header {
      width: 100%;
      padding: 20px 15px;

      @media (max-width: 768px) {
        padding: 15px 10px;
        font-size: 18px;
        font-weight: 600;
      }
    }

    .table {
      width: 100%;
      height: calc(100% - 200px);
      overflow-x: auto;

      @media (max-width: 768px) {
        height: auto;
        overflow-x: visible;
        min-height: calc(100vh - 300px);
      }
    }

    .footer {
      display: flex;
      flex-direction: row;
      width: 100%;
      justify-content: space-between;
      align-items: center;
      padding: 10px 5px;
      flex-wrap: wrap;
      gap: 10px;

      @media (max-width: 768px) {
        flex-direction: column;
        gap: 15px;
        padding: 15px 10px;
      }

      .totals {
        @media (max-width: 768px) {
          font-size: 12px;
          text-align: center;
          width: 100%;
          order: 3;
        }
      }

      .pages {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 8px;

        @media (max-width: 768px) {
          justify-content: center;
          width: 100%;
          font-size: 14px;
        }

        div {
          width: 75px;

          @media (max-width: 768px) {
            width: auto;
            min-width: 50px;
            text-align: center;
          }
        }

        select {
          @media (max-width: 768px) {
            padding: 6px 8px;
            font-size: 14px;
          }
        }

        .page-link {
          color: $link-color-1;
          cursor: pointer;
          padding: 4px 8px;

          @media (max-width: 768px) {
            padding: 8px 12px;
            background-color: #f3f4f6;
            border-radius: 6px;
            font-size: 14px;
            min-width: 60px;
            display: inline-block;
            text-align: center;
          }

          &.disabled {
            opacity: 0.5;
            pointer-events: none;
          }
        }
      }
    }
  }
}

.dept-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  background: #e0f2f1;
  color: #0f766e;
  font-size: 12px;
  font-weight: 600;
}

.stats-content {
  padding: 0 15px 30px;

  @media (max-width: 768px) {
    padding: 0 10px 20px;
  }
}

.stats-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
  margin-bottom: 20px;

  .stat-card {
    background: white;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
    padding: 14px 16px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);

    .stat-label {
      font-size: 12px;
      text-transform: uppercase;
      color: #6b7280;
    }

    .stat-value {
      font-size: 24px;
      font-weight: 700;
      margin: 4px 0;
    }

    .stat-hint {
      font-size: 12px;
      color: #9ca3af;
    }
  }
}

.stats-section {
  margin-bottom: 24px;

  .stats-section-title {
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .inline-select {
    font-size: 13px;
    padding: 4px 8px;
  }

  .clickable-row {
    cursor: pointer;
  }
}

/* Reembolsos (filas negativas) */
.refund-amount {
  color: #b91c1c;
  font-weight: 800;
  white-space: nowrap;
}
.refund-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 9999px;
  background: #dc2626;
  color: #fff;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  white-space: nowrap;
  vertical-align: middle;
}
.refund-card {
  border-color: #fca5a5 !important;
  box-shadow: inset 5px 0 0 #dc2626;
}
.refund-row {
  background-color: #fee2e2 !important;
  box-shadow: inset 5px 0 0 #dc2626;
}
.refund-type {
  color: #b91c1c;
  font-weight: 700;
}
.refund-card {
  background-color: #fef2f2 !important;
}
.refund-text {
  color: #b91c1c !important;
  font-weight: 700;
}
.stats-cards .stat-card--refund {
  background: #fef2f2;
  border-color: #fca5a5;
  box-shadow: inset 5px 0 0 #dc2626;
}
</style>
